import React, { useRef, useState, useEffect } from 'react';
import { uploadImageToS3 } from '@/Data/Image';
import './index.scss';

interface EditableFileFieldProps {
    value: string | null;
    onChange: (url: string | null) => void;
    accept?: string;
    placeholder?: React.ReactNode;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

const EditableFileField: React.FC<EditableFileFieldProps> = ({
    value,
    onChange,
    accept = 'image/jpeg,image/png,image/jpg',
    placeholder,
    disabled = false,
    className = '',
    style,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    // Update preview when value changes externally
    useEffect(() => {
        if (value && !previewUrl) {
            setPreviewUrl(value);
        }
    }, [value, previewUrl]);

    const handleClick = () => {
        if (!disabled && !uploading) {
            fileInputRef.current?.click();
        }
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        // Create local preview immediately
        const localPreviewUrl = URL.createObjectURL(file);
        setPreviewUrl(localPreviewUrl);

        // Upload to S3
        setUploading(true);
        try {
            const s3Url = await uploadImageToS3(localPreviewUrl, file.name);
            if (s3Url) {
                onChange(s3Url);
            } else {
                // Upload failed, revert preview
                setPreviewUrl(value);
                onChange(null);
            }
        } catch (e) {
            console.error('Failed to upload image:', e);
            setPreviewUrl(value);
            onChange(null);
        } finally {
            setUploading(false);
            // Reset input so same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        setPreviewUrl(null);
        onChange(null);
    };

    const displayUrl = previewUrl || value;

    return (
        <div
            className={`editable-file-field ${className} ${disabled ? 'disabled' : ''} ${uploading ? 'uploading' : ''}`}
            style={style}
            onClick={handleClick}
        >
            {displayUrl ? (
                <div className="preview-container">
                    <img src={displayUrl} alt="Preview" className="preview-image" />
                    {uploading && (
                        <div className="upload-overlay">
                            <span>Uploading...</span>
                        </div>
                    )}
                    {!uploading && !disabled && (
                        <div className="change-overlay">
                            <span>Ganti Foto</span>
                        </div>
                    )}
                    {!uploading && !disabled && (
                        <button className="remove-btn" onClick={handleRemove}>
                            <span>×</span>
                        </button>
                    )}
                </div>
            ) : (
                <div className="placeholder-container">
                    {placeholder || (
                        <>
                            <img src="/img/add_avatar.svg" alt="Upload" className="upload-icon" />
                            <span>Upload Foto</span>
                        </>
                    )}
                </div>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={disabled || uploading}
            />
        </div>
    );
};

export default EditableFileField;
